import { useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import {
  ArrowLeft,
  Upload,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  FileText,
  Trash2,
  Sparkles,
} from "lucide-react";

const MESI = [
  "", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

export default function ImportBlocco() {
  const nav = useNavigate();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [clienti, setClienti] = useState([]);
  const [confirming, setConfirming] = useState(false);

  // load anagrafica clienti once when needed
  const loadClienti = useCallback(async () => {
    if (clienti.length) return;
    const r = await api.get("/clienti");
    setClienti(r.data);
  }, [clienti.length]);

  const handleFiles = async (files) => {
    const arr = Array.from(files || []).filter((f) => {
      const n = f.name.toLowerCase();
      return n.endsWith(".pdf") || n.endsWith(".zip");
    });
    if (arr.length === 0) return;
    setBusy(true);
    setError("");
    setProgress(0);
    try {
      await loadClienti();
      const fd = new FormData();
      for (const f of arr) fd.append("files", f);
      const r = await api.post("/import-blocco/preview", fd, {
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setSessionId(r.data.session_id);
      setRows(
        r.data.rows.map((row) => ({
          ...row,
          excluded: false,
        })),
      );
      setSummary(r.data.summary);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore durante l'analisi PDF.");
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const canImport = useMemo(() => {
    return (
      rows.length > 0 &&
      rows.every(
        (r) =>
          r.excluded ||
          (r.cliente_codice && r.anno && r.mese >= 1 && r.mese <= 12),
      )
    );
  }, [rows]);

  const includedCount = rows.filter((r) => !r.excluded).length;

  const groupedSummary = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      if (r.excluded || !r.cliente_codice) continue;
      const key = `${r.cliente_codice}_${r.anno}_${r.mese}`;
      if (!m.has(key)) {
        m.set(key, {
          codice: r.cliente_codice,
          nome:
            clienti.find((c) => c.codice === r.cliente_codice)?.nome ||
            r.cliente_nome,
          anno: r.anno,
          mese: r.mese,
          n_pdf: 0,
        });
      }
      m.get(key).n_pdf += 1;
    }
    return Array.from(m.values()).sort((a, b) => {
      const k = a.nome.localeCompare(b.nome);
      return k || a.anno - b.anno || a.mese - b.mese;
    });
  }, [rows, clienti]);

  const confirmImport = async () => {
    setConfirming(true);
    setError("");
    try {
      const payload = {
        session_id: sessionId,
        rows: rows.map((r) => ({
          pdf_filename: r.pdf_filename,
          cliente_codice: r.cliente_codice,
          anno: r.anno ? Number(r.anno) : null,
          mese: r.mese ? Number(r.mese) : null,
          excluded: r.excluded,
        })),
      };
      const r = await api.post("/import-blocco/conferma", payload);
      const n = r.data.distributed;
      const acqs = r.data.by_acquisizione.length;
      sessionStorage.setItem(
        "pa_toast",
        JSON.stringify({
          type: "ok",
          text: `${n} PDF importati in ${acqs} acquisizioni.`,
        }),
      );
      nav("/", { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore durante l'import.");
    } finally {
      setConfirming(false);
    }
  };

  const resetSession = () => {
    setSessionId(null);
    setRows([]);
    setSummary(null);
    setError("");
  };

  return (
    <div data-testid="import-blocco-page">
      <Header />
      <div className="pa-container" style={{ padding: "28px 24px 64px" }}>
        <button
          onClick={() => nav("/")}
          data-testid="back-to-dashboard"
          style={{
            background: "transparent",
            border: "none",
            color: "var(--pa-muted)",
            fontSize: 13,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
            padding: 0,
            marginBottom: 18,
          }}
        >
          <ArrowLeft size={14} /> Dashboard
        </button>

        <h1
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: "var(--pa-navy)",
            margin: 0,
            letterSpacing: "-0.01em",
          }}
        >
          Import in blocco
        </h1>
        <p style={{ color: "var(--pa-muted)", fontSize: 14, margin: "4px 0 24px" }}>
          Carica più PDF in una sola operazione. Il portale identifica
          automaticamente cliente, anno e mese dal contenuto di ogni file.
        </p>

        {error && (
          <div
            style={{
              background: "var(--pa-red-bg)",
              color: "var(--pa-red)",
              padding: "10px 14px",
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 13,
            }}
            data-testid="import-error"
          >
            {error}
          </div>
        )}

        {/* STEP 1: Upload */}
        {!sessionId && (
          <div
            className={`pa-dropzone ${dragOver ? "dragover" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            data-testid="import-dropzone"
            style={{ padding: 56 }}
          >
            <Sparkles size={28} color="var(--pa-turquoise)" style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 16, color: "var(--pa-text)", fontWeight: 600 }}>
              {busy
                ? `Analisi in corso… ${progress > 0 ? progress + "%" : ""}`
                : "Trascina qui i PDF (o un file ZIP) o clicca per selezionarli"}
            </div>
            <div style={{ fontSize: 13, color: "var(--pa-muted)", marginTop: 4 }}>
              Accetta PDF singoli oppure archivi ZIP di PDF. Vengono riconosciuti
              automaticamente tramite codice fiscale e/o denominazione.
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,.pdf,application/zip,.zip"
              multiple
              onChange={(e) => handleFiles(e.target.files)}
              style={{ display: "none" }}
              data-testid="import-file-input"
            />
          </div>
        )}

        {/* STEP 2: Classification table */}
        {sessionId && (
          <>
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 18,
              }}
            >
              <Stat
                label="Riconosciuti"
                value={summary?.riconosciuti || 0}
                color="var(--pa-green)"
                testid="stat-riconosciuti"
              />
              <Stat
                label="Da confermare"
                value={summary?.da_confermare || 0}
                color="var(--pa-amber)"
                testid="stat-da-confermare"
              />
              <Stat
                label="Non riconosciuti"
                value={summary?.non_riconosciuti || 0}
                color="var(--pa-red)"
                testid="stat-non-riconosciuti"
              />
              <Stat
                label="Inclusi nell'import"
                value={includedCount}
                color="var(--pa-navy)"
                testid="stat-inclusi"
              />
            </div>

            <div
              className="pa-card"
              style={{ padding: 0, overflow: "auto", marginBottom: 18 }}
              data-testid="classification-table-wrap"
            >
              <table className="pa-table" data-testid="classification-table">
                <thead>
                  <tr>
                    <th style={{ width: 30 }}></th>
                    <th>File</th>
                    <th>Stato</th>
                    <th style={{ minWidth: 260 }}>Cliente</th>
                    <th style={{ minWidth: 140 }}>Periodo</th>
                    <th style={{ width: 60, textAlign: "center" }}>Azione</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <ClassificationRow
                      key={row.pdf_filename}
                      row={row}
                      idx={idx}
                      clienti={clienti}
                      onChange={(patch) => updateRow(idx, patch)}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Recap acquisizioni */}
            {groupedSummary.length > 0 && (
              <div className="pa-card" style={{ padding: 18, marginBottom: 18 }} data-testid="recap-card">
                <h3
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    color: "var(--pa-muted)",
                    letterSpacing: "0.06em",
                    margin: "0 0 10px",
                  }}
                >
                  Riepilogo acquisizioni (verranno create o aggiornate {groupedSummary.length})
                </h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                    gap: 10,
                  }}
                >
                  {groupedSummary.map((g) => (
                    <div
                      key={`${g.codice}-${g.anno}-${g.mese}`}
                      style={{
                        padding: "10px 12px",
                        background: "#FAFBFC",
                        border: "1px solid var(--pa-border)",
                        borderRadius: 8,
                        fontSize: 13,
                      }}
                    >
                      <div style={{ fontWeight: 600, color: "var(--pa-text)" }}>
                        {g.nome}
                      </div>
                      <div style={{ color: "var(--pa-muted)", fontSize: 12, marginTop: 2 }}>
                        {MESI[g.mese]} {g.anno} · <strong>{g.n_pdf}</strong> PDF
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                onClick={confirmImport}
                disabled={!canImport || confirming || includedCount === 0}
                className="pa-btn-accent"
                data-testid="import-confirm-button"
                style={{
                  padding: "12px 22px",
                  borderRadius: 8,
                  fontWeight: 600,
                  border: "none",
                  cursor: canImport && includedCount > 0 ? "pointer" : "not-allowed",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  opacity: canImport && includedCount > 0 ? 1 : 0.55,
                }}
              >
                {confirming ? <span className="pa-spinner" /> : <Upload size={16} />}
                {confirming ? "Importazione in corso…" : `Importa ${includedCount} PDF`}
              </button>
              <button
                onClick={resetSession}
                data-testid="import-reset-button"
                style={{
                  padding: "12px 18px",
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: "pointer",
                  background: "transparent",
                  border: "1px solid var(--pa-border)",
                  color: "var(--pa-muted)",
                }}
              >
                Annulla e ricomincia
              </button>
            </div>
            {!canImport && (
              <p style={{ color: "var(--pa-amber)", fontSize: 13, marginTop: 12 }}>
                Alcune righe non hanno cliente/periodo. Completa le selezioni
                oppure escludile per procedere.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ClassificationRow({ row, idx, clienti, onChange }) {
  const statoCfg = {
    riconosciuto: {
      bg: "var(--pa-green-bg)",
      fg: "var(--pa-green)",
      icon: <CheckCircle2 size={13} />,
      label: "Riconosciuto",
    },
    da_confermare: {
      bg: "var(--pa-amber-bg)",
      fg: "var(--pa-amber)",
      icon: <AlertTriangle size={13} />,
      label: "Da confermare",
    },
    non_riconosciuto: {
      bg: "var(--pa-red-bg)",
      fg: "var(--pa-red)",
      icon: <XCircle size={13} />,
      label: "Non riconosciuto",
    },
  }[row.stato] || {
    bg: "var(--pa-grey-bg)",
    fg: "var(--pa-muted)",
    icon: null,
    label: row.stato,
  };

  const excluded = row.excluded;

  return (
    <tr
      data-testid={`row-${idx}`}
      style={{ opacity: excluded ? 0.4 : 1 }}
    >
      <td style={{ paddingLeft: 16 }}>
        <input
          type="checkbox"
          checked={!excluded}
          onChange={(e) => onChange({ excluded: !e.target.checked })}
          data-testid={`include-${idx}`}
          title={excluded ? "Includi nell'import" : "Escludi dall'import"}
          style={{ cursor: "pointer" }}
        />
      </td>
      <td>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            maxWidth: 280,
          }}
        >
          <FileText size={14} color="#6B7280" style={{ flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontWeight: 500,
                fontSize: 13,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 260,
              }}
              title={row.nome_originale}
            >
              {row.nome_originale}
            </div>
            {row.codice_fiscale && (
              <div style={{ fontSize: 11, color: "var(--pa-muted)" }}>
                CF {row.codice_fiscale}
              </div>
            )}
          </div>
        </div>
      </td>
      <td>
        <span
          title={(row.note || []).join(" · ")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "3px 8px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            background: statoCfg.bg,
            color: statoCfg.fg,
          }}
          data-testid={`stato-${idx}`}
        >
          {statoCfg.icon} {statoCfg.label}
        </span>
      </td>
      <td>
        <select
          value={row.cliente_codice || ""}
          onChange={(e) => onChange({ cliente_codice: e.target.value || null })}
          disabled={excluded}
          data-testid={`cliente-${idx}`}
          style={{
            width: "100%",
            padding: "6px 8px",
            border: "1px solid var(--pa-border)",
            borderRadius: 6,
            fontSize: 13,
            background: "white",
            cursor: excluded ? "not-allowed" : "pointer",
          }}
        >
          <option value="">— seleziona cliente —</option>
          {clienti.map((c) => (
            <option key={c.codice} value={c.codice}>
              {c.nome} ({c.codice})
            </option>
          ))}
        </select>
      </td>
      <td>
        <div style={{ display: "flex", gap: 6 }}>
          <select
            value={row.mese || ""}
            onChange={(e) =>
              onChange({ mese: e.target.value ? Number(e.target.value) : null })
            }
            disabled={excluded}
            data-testid={`mese-${idx}`}
            style={{
              padding: "6px 6px",
              border: "1px solid var(--pa-border)",
              borderRadius: 6,
              fontSize: 13,
              background: "white",
              flex: 1,
            }}
          >
            <option value="">Mese</option>
            {MESI.slice(1).map((m, i) => (
              <option key={i + 1} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
          <input
            type="number"
            value={row.anno || ""}
            onChange={(e) =>
              onChange({ anno: e.target.value ? Number(e.target.value) : null })
            }
            disabled={excluded}
            data-testid={`anno-${idx}`}
            placeholder="Anno"
            style={{
              width: 78,
              padding: "6px 6px",
              border: "1px solid var(--pa-border)",
              borderRadius: 6,
              fontSize: 13,
            }}
          />
        </div>
      </td>
      <td style={{ textAlign: "center" }}>
        <button
          onClick={() => onChange({ excluded: true })}
          disabled={excluded}
          title="Escludi"
          data-testid={`exclude-${idx}`}
          style={{
            background: "transparent",
            border: "none",
            color: "var(--pa-red)",
            cursor: excluded ? "not-allowed" : "pointer",
            padding: 4,
            opacity: excluded ? 0.4 : 1,
          }}
        >
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  );
}

function Stat({ label, value, color, testid }) {
  return (
    <div className="pa-stat" data-testid={testid} style={{ minWidth: 140 }}>
      <span className="pa-stat-label">{label}</span>
      <span className="pa-stat-value" style={{ color }}>
        {value}
      </span>
    </div>
  );
}
